import {
  backButton,
  hapticFeedback,
  init,
  isTMA,
  mainButton,
  miniApp,
  openLink,
  openTelegramLink,
  retrieveRawInitData,
  swipeBehavior,
  themeParams,
  viewport,
} from '@tma.js/sdk-react';

/** Runs a Telegram call that may be unsupported on this client version; a missing feature must not break the app. */
function safe(fn: () => unknown) {
  try {
    fn();
  } catch {
    // unsupported in this Telegram version or outside Telegram
  }
}

export const inTelegram: boolean = (() => {
  try {
    return isTMA();
  } catch {
    return false;
  }
})();

function cssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() as `#${string}`;
}

function applyTheme(dark: boolean) {
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  if (!inTelegram) return;
  const bg = cssVar('--ul-bg');
  safe(() => miniApp.setHeaderColor(bg));
  safe(() => miniApp.setBgColor(bg));
  safe(() => miniApp.setBottomBarColor(bg));
  safe(() => mainButton.setParams({ bgColor: cssVar('--ul-accent'), textColor: cssVar('--ul-on-accent') }));
}

export type ThemePref = 'auto' | 'light' | 'dark';
const THEME_KEY = 'uplink.theme';

/** The user's choice on this device; "auto" follows Telegram (or the system outside it). */
let themePref: ThemePref = (() => {
  try {
    const v = localStorage.getItem(THEME_KEY);
    // The website is dark unless the person chose otherwise; the Mini App follows Telegram
    return v === 'light' || v === 'dark' ? v : inTelegram ? 'auto' : 'dark';
  } catch {
    return 'auto';
  }
})();
let systemDark = false;
const renderTheme = () => applyTheme(themePref === 'auto' ? systemDark : themePref === 'dark');

export const getThemePreference = () => themePref;
export function setThemePreference(pref: ThemePref) {
  themePref = pref;
  try {
    localStorage.setItem(THEME_KEY, pref);
  } catch {}
  renderTheme();
}

/** Call once before rendering. Mounts Telegram features and syncs the theme (design-system: theme comes from Telegram). */
export function bootstrapTelegram() {
  if (!inTelegram) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    systemDark = mq.matches;
    renderTheme();
    mq.addEventListener('change', (e) => {
      systemDark = e.matches;
      renderTheme();
    });
    return;
  }
  init();
  safe(() => themeParams.mount());
  safe(() => miniApp.mount());
  safe(() => backButton.mount());
  safe(() => mainButton.mount());
  safe(() => swipeBehavior.mount());
  safe(() => swipeBehavior.disableVertical());
  viewport
    .mount()
    .then(() => viewport.expand())
    .catch(() => {});
  systemDark = miniApp.isDark();
  renderTheme();
  safe(() =>
    miniApp.isDark.sub((dark: boolean) => {
      systemDark = dark;
      renderTheme();
    }),
  );
  safe(() => miniApp.ready());
}

const DEV_USER_KEY = 'uplink.devUserId';
const WEB_MODE_KEY = 'uplink.webMode';

/** Локальный Telegram ID, общий для режима Mini App и тестового входа на сайте. */
export function devUserId(): number {
  let id = 1001;
  try {
    const stored = Number(localStorage.getItem(DEV_USER_KEY));
    if (Number.isSafeInteger(stored) && stored > 0) id = stored;
  } catch {}
  return id;
}

/**
 * In development a plain browser signs in as a dev user; `?web=1` switches to the real website sign-in
 * (cookie sessions) and `?web=0` back. In production the browser is always the website.
 */
export const webSignIn: boolean = (() => {
  if (inTelegram) return false;
  if (!import.meta.env.DEV) return true;
  try {
    const flag = new URLSearchParams(window.location.search).get('web');
    if (flag === '1') localStorage.setItem(WEB_MODE_KEY, '1');
    if (flag === '0') localStorage.removeItem(WEB_MODE_KEY);
    return localStorage.getItem(WEB_MODE_KEY) === '1';
  } catch {
    return false;
  }
})();

/** `tma <initData>` inside Telegram; `dev <id>` in a plain browser during development (ADR-008). */
export function authHeader(): string | undefined {
  if (inTelegram) {
    const raw = retrieveRawInitData();
    return raw ? `tma ${raw}` : undefined;
  }
  if (import.meta.env.DEV && !webSignIn) {
    return `dev ${devUserId()}`;
  }
  return undefined;
}

export function openPayUrl(url: string) {
  if (inTelegram && url.startsWith('https://t.me/')) return safe(() => openTelegramLink(url));
  if (inTelegram) return safe(() => openLink(url));
  window.open(url, '_blank', 'noopener');
}

/** Прямая ссылка открывает Main Mini App бота, а не только его чат. */
export function telegramMiniAppUrl(botUrl: string): string {
  const url = new URL(botUrl);
  url.pathname = url.pathname.replace(/\/$/, '');
  if (!url.searchParams.has('startapp')) url.searchParams.append('startapp', '');
  return url.toString().replace(/startapp=(?=&|$)/, 'startapp');
}

/** The support chat if one is set, otherwise back to the bot chat the app was opened from. */
export function openSupport(url?: string | null) {
  if (url) return openPayUrl(url);
  if (inTelegram) return safe(() => miniApp.close());
}

/** Back to the bot chat the app was opened from. */
export function closeApp() {
  if (inTelegram) safe(() => miniApp.close());
}

/** A Telegram chat by @username, inside Telegram when possible. */
export function openChat(username: string) {
  openPayUrl(`https://t.me/${username}`);
}

export const haptic = {
  tap: () => inTelegram && safe(() => hapticFeedback.impactOccurred('light')),
  select: () => inTelegram && safe(() => hapticFeedback.selectionChanged()),
  success: () => inTelegram && safe(() => hapticFeedback.notificationOccurred('success')),
  error: () => inTelegram && safe(() => hapticFeedback.notificationOccurred('error')),
};

export { backButton, mainButton };
